import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/utils";
import { Lock, Package, Plus, TestTube } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

export interface ProductCardProps {
	id: string;
	name: string;
	brand: string;
	description?: string;
	price: number;
	image: string;
	category: string;
	categoryId?: string | null;
	tags?: string[];
	ingredients?: string[];
	supplierId: string;
	msrp?: number;
	caseQuantity: number;
	sampleAvailable?: boolean;
	sampleQuantity?: number;
	sampleCost?: number;
	totalViews?: number;
}

export function ProductCard({
	id,
	name,
	brand,
	price,
	image,
	category,
	supplierId,
	msrp,
	caseQuantity = 1,
	sampleAvailable = false,
	sampleQuantity = 1,
	sampleCost = 0,
	totalViews,
}: ProductCardProps) {
	const { addToCart } = useCart();
	const { toast } = useToast();
	const [isAdding, setIsAdding] = useState(false);
	const { user } = useAuth();

	const handleAddToCart = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		setIsAdding(true);

		addToCart({
			id,
			name,
			price,
			quantity: 1,
			image,
			supplierId,
			caseQuantity,
		});

		// Show toast notification
		toast({
			title: "Added to cart",
			description: `${name} has been added to your cart`,
		});

		// Reset adding state after animation
		setTimeout(() => {
			setIsAdding(false);
		}, 500);
	};

	return (
		<Link to={`/products/${id}`}>
			<Card className="h-full overflow-hidden group hover:shadow-md transition-shadow flex flex-col">
				<div className="relative aspect-square overflow-hidden bg-gray-50">
					{/* Badges */}
					<div className="absolute top-2 left-2 flex flex-col gap-1 z-10">
						{sampleAvailable && (
							<Badge className="bg-blue-500 flex items-center gap-1">
								<TestTube className="h-3 w-3" />
								Sample Available
							</Badge>
						)}
					</div>

					{/* Add to cart button overlay */}
					{user && (
						<Button
							onClick={handleAddToCart}
							size="icon"
							className="absolute right-2 top-2 opacity-0 group-hover:opacity-75 transition-opacity z-10"
							disabled={isAdding}>
							{isAdding ? (
								<span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
							) : (
								<Plus className="h-4 w-4" />
							)}
						</Button>
					)}

					{/* Product image */}
					<img
						src={image}
						alt={name}
						className="h-full w-full object-cover transition-transform group-hover:scale-105"
					/>
				</div>

				<CardContent className="p-4 flex-grow">
					<div className="text-sm text-gray-500 mb-1">{brand}</div>
					<h3 className="font-medium text-gray-900 mb-1 line-clamp-2">
						{name}
					</h3>
					<div className="flex flex-col">
						{user ? (
							<span className="font-medium text-lg">
								{formatCurrency(price)}
							</span>
						) : (
							<div className="flex items-center gap-1.5 text-gray-500 font-medium">
								<Lock className="h-4 w-4" />
								<span className="text-sm">
									Login for wholesale price
								</span>
							</div>
						)}
						{msrp && msrp > price && (
							<span className="text-sm text-gray-400">
								MSRP: {formatCurrency(msrp)}
							</span>
						)}
					</div>
					<div className="flex flex-col mt-1">
						{caseQuantity > 1 && (
							<div className="text-xs text-gray-500 flex items-center">
								<Package className="h-3 w-3 mr-1" />
								{caseQuantity} units per case
							</div>
						)}
					</div>
				</CardContent>
			</Card>
		</Link>
	);
}
